import React from "react";
import "./EventRequest.scss";
import { useMutation, useQuery } from "react-query";
import { useEffect, useState, memo } from "react";
import { formatDate } from "../../utils/dateFormatter";
import useAuth from "../../core/hooks/useAuth";
import { DatePicker, Input, Select, Spin } from "antd";
import CustomAccordion from "./CustomAccordion";
import { makeDelay } from "../../core/utils/helpers";
import Pagination from "../../components/Pagination/Pagination";
import useDebounce from "../../core/hooks/useDebounce";
import eventRequestService from "./logic/useEventRequestService";

const EventRequest = () => {
  const [date, setDate] = useState(null);
  const [status, setStatus] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);


  const { authenticatedUser } = useAuth();
  const statusOptions = [
    { value: "pending", label: "pending" },
    { value: "accepted", label: "accepted" },
    { value: "rejected", label: "rejected" },
  ];

  const handleDateChange = (date, dateString) => {
    if (dateString === "" || date === null) {
      setDate(null);
    } else {
      const formattedDate = new Date(date).toISOString().split("T")[0];
      setDate(formattedDate);
    }
  };

  const handleStatusChange = (value) => {
    setStatus(value || "");
  };

  const handleSearchChange = (event) => {
    makeDelay(() => {
      if (event.target.value.length >= 2) {
        setSearchQuery(event.target.value);
        setPage(1);
      } else {
        setSearchQuery("");
      }
    }, 750);
  };

  const createFilterObject = (date, status) => {
    const filterObject = {};
    if (date) {
      filterObject.date = date;
    }

    if (status) {
      filterObject.status = status;
    }

    return filterObject;
  };
  const handleNext = () => {
    setPage((page) => page + 1);
  };

  const handlePrevious = () => {
    setPage((page) => page - 1);
  };

  const { debouncedValue: searchDebouncedValue } = useDebounce(searchQuery, 1000);

  const {
    data: eventRequests,
    isFetching,
    refetch: refetchEventRequest,
  } = useQuery(
    [
      "requests",
      createFilterObject(date, status),
      page,
      searchDebouncedValue,
    ],
    () =>
    eventRequestService.getAllEventRequestsData(
        createFilterObject(date, status),
        page,
        6,
        searchDebouncedValue,
      ),
    {
      initialData: {
        data: { eventRequests: [], meta: { hasPrevious: false, hasNext: false } },
      },
      keepPreviousData: true,
      staleTime: 0,
    }
  );

  useEffect(() => {
    refetchEventRequest();
    setPage(1);
  }, [date, status, refetchEventRequest]);
   const { mutate: updateRequestStatus } = useMutation(
    (requestId) =>
      eventRequestService.updateEventRequest(requestId, {
        status: "accepted",
        approvedBy: authenticatedUser?.id,
      }),
    {
      onSettled: () => {
        refetchEventRequest();
      },
    }
  );

  const handleAcceptRequest = (requestId) => {
    updateRequestStatus(requestId);
  };

  const { mutate: updateRequestStatusToRejected } = useMutation(
    (requestId) =>
      eventRequestService.updateEventRequest(requestId, {
        status: "rejected",
        approvedBy: authenticatedUser?.id,
      }),
    {
      onSettled: () => {
        refetchEventRequest();
      },
    }
  );

  const handleRejectRequest = (requestId) => {
    updateRequestStatusToRejected(requestId);
  };

  const modifiedData = eventRequests?.data?.data?.map((request) => {
    const date = formatDate(request?.event?.date);
    const createdDate = formatDate(request.createdAt);
    const modifiedDate = formatDate(request?.updatedAt);

    return {
      id: request.id,
      headerLabels: [
        request?.event?.title,
        request.status,
        date,
        request.user?.firstName + " " + request?.user?.lastName,
        request?.event?.location,
      ],
      actionButtons: ["Accept", "Decline"],
      content: ["Request Description", request?.event?.description],
      status: request.status,
      additionalInformation: {
        location: request.event?.location,
        created: createdDate,
        lastModified: modifiedDate,
        approvedBy: request.approver
          ? request.approver?.firstName + " " + request.approver?.lastName
          : "",
      },
    };
  });

  return (
    <div className="event-request">
      <div className="request-number">
        <label>
          Total Requests : {eventRequests ? eventRequests?.data?.meta?.itemCount : 0}
        </label>
      </div>
      <div className="request-search-event">
        <Select
          allowClear
          className="ant-select-selector"
          placeholder="Select status"
          options={statusOptions}
          onChange={(value) => {
            handleStatusChange(value);
          }}
        />

        <DatePicker className="date" onChange={handleDateChange} />
        <div className="search-container">
          <Input.Search
            placeholder="Search..."
            onChange={handleSearchChange}
            style={{ width: "300px" }}
            className="search-input"
          />
        </div>
      </div>
      {eventRequests?.data?.meta?.itemCount === 0 ? ( 
        <div className="no-data-message">No data</div>
      ) : (



      <div className="accordion-container">
        <Spin spinning={isFetching} size="large">

        {modifiedData?.map((requestData, index) => (
          <CustomAccordion key={index}
          data={requestData}
          meta={eventRequests?.data.meta}
          handleNext={handleNext}
          handlePrevious={handlePrevious}
          handleAcceptRequest={handleAcceptRequest}
          handleRejectRequest={handleRejectRequest}
          />
        ))}
        </Spin>
      </div>
      )}
      <Pagination
            hasNextPage={eventRequests?.data?.meta.hasNextPage}
            hasPreviousPage={eventRequests?.data?.meta.hasPreviousPage}
            handleNext={handleNext}
            handlePrevious={handlePrevious}
            currentPage={eventRequests?.data?.meta?.page}
            totalPageCount={eventRequests?.data?.meta?.pageCount}
      />
    </div>
  );
};

export default memo(EventRequest);
